/**
 * OnboardingScreen — Forest theme, deep radial gradient, grasshopper logo mark
 * - Background: radial gradient, deep green to near-black
 * - Logo: grasshopper mark (logo-mark.svg), no glow/ring
 * - btn-primary gets glow shadow
 * - btn-secondary matches surface-2 style
 * ALL business logic (import flow, PIN step, store calls) — UNCHANGED.
 */

import { useState } from 'react'
import { PrivateKey } from '@wharfkit/antelope'
import {
  accountManager,
  RESOLVE_ERROR_NETWORK,
  RESOLVE_ERROR_ACCOUNT_NOT_FOUND,
} from '@properwallet/wallet-core'
import { EOS_RPC_ENDPOINTS } from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { useTranslation } from '../hooks/useTranslation'
import { PIN_LENGTH } from '../components/PinInput'
import PinInput from '../components/PinInput'
import Logo from '../components/Logo'
import LanguageSwitcher from '../components/LanguageSwitcher'

type Step = 'choose' | 'import' | 'postImportPin'

const RPC_PRESETS = [
  EOS_RPC_ENDPOINTS[0],
  'https://eos.greymass.com',
  'https://api.eosn.io',
  'https://eos.dfuse.eosnation.io',
  'https://api.main.alohaeos.com',
]

function isLikelyInvalidKeyMessage(msg: string): boolean {
  const m = msg.toLowerCase()
  return (
    m.includes('privatekey') ||
    m.includes('private key') ||
    m.includes('invalid key') ||
    m.includes('checksum') ||
    m.includes('unrecognized') ||
    m.includes('expected') ||
    m.includes('decode')
  )
}

export default function OnboardingScreen() {
  const { t } = useTranslation()
  const { navigate, init, encryptPinForNewAccount, unlockWithPin } = useWalletStore()
  const { onboardingMode, setOnboardingMode, rpcUrl, setRpcUrl } = useAppStore()
  const [step, setStep] = useState<Step>('choose')
  const [privateKey, setPrivateKey] = useState('')
  const [walletLabel, setWalletLabel] = useState('')
  const [error, setError] = useState('')
  const [postPin, setPostPin] = useState('')
  const [importBusy, setImportBusy] = useState(false)
  const [pendingAccountName, setPendingAccountName] = useState('')
  const [rpcSheetOpen, setRpcSheetOpen] = useState(false)
  const [customRpc, setCustomRpc] = useState(rpcUrl)

  // ── All business logic UNCHANGED ──────────────────────────────
  const handleImport = async () => {
    setError('')
    if (!privateKey.trim()) { setError(t('onboarding.enterPrivateKey')); return }
    const labelTrim = walletLabel.trim()
    if (!labelTrim) { setError(t('onboarding.walletLabelRequired')); return }
    if (labelTrim.length > 64) { setError(t('onboarding.walletLabelTooLong')); return }
    try { PrivateKey.from(privateKey.trim()) }
    catch { setError(t('onboarding.errorInvalidKey')); return }
    setImportBusy(true)
    try {
      const account = await accountManager.addAccount(privateKey.trim(), { label: labelTrim })
      setPendingAccountName(account.name)
      await init()
      const hasPin = await accountManager.hasPin()
      if (onboardingMode === 'add-account' && hasPin) { setStep('postImportPin'); setPostPin('') }
      else if (onboardingMode === 'add-account') { setOnboardingMode('fresh'); navigate('wallet') }
      else { navigate(hasPin ? 'wallet' : 'setup-pin') }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      if (msg === RESOLVE_ERROR_NETWORK) setError(t('onboarding.errorNetwork'))
      else if (msg === RESOLVE_ERROR_ACCOUNT_NOT_FOUND) setError(t('onboarding.errorAccountNotFound'))
      else if (isLikelyInvalidKeyMessage(msg)) setError(t('onboarding.errorInvalidKey'))
      else setError(msg || t('onboarding.importError'))
    } finally { setImportBusy(false) }
  }

  const handlePostImportPin = async (pin: string) => {
    const name = pendingAccountName
    if (!name || pin.length !== PIN_LENGTH) return
    setError('')
    try {
      await encryptPinForNewAccount(name, pin)
      await unlockWithPin(pin)
      setOnboardingMode('fresh')
      navigate('wallet')
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'))
      setPostPin('')
    }
  }

  const onPostPinChange = (v: string) => {
    setPostPin(v)
    setError('')
    if (v.length === PIN_LENGTH) void handlePostImportPin(v)
  }

  const handleBackFromImport = () => {
    if (onboardingMode === 'add-account') { setOnboardingMode('fresh'); navigate('wallet') }
    else { setStep('choose') }
  }

  // ── POST-IMPORT PIN STEP ────────────────────────────────────
  if (step === 'postImportPin') {
    return (
      <div
        className="flex flex-col h-full pt-safe px-4 pb-8 justify-center"
        style={{ background: '#000000' }}
      >
        <div className="flex justify-center mb-4">
          <Logo size="xl" />
        </div>
        <h2 className="text-xl font-semibold text-white text-center mb-2">{t('pin.enter')}</h2>
        <p className="text-sm text-center mb-8" style={{ color: '#6b7280' }}>{t('pin.postImportHint')}</p>
        <PinInput value={postPin} onChange={onPostPinChange} />
        {error && <p className="text-danger text-sm text-center mt-6">{error}</p>}
        <button
          type="button"
          className="mt-8 text-sm"
          style={{ color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
          onClick={() => navigate('wallet')}
        >
          {t('common.cancel')}
        </button>
      </div>
    )
  }

  // ── IMPORT STEP ─────────────────────────────────────────────
  if (step === 'import') {
    return (
      <div
        className="relative flex flex-col h-full pt-safe"
        style={{ background: '#000000' }}
      >
        {importBusy && (
          <div className="fixed inset-0 z-40 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
            <div className="text-center space-y-4 px-6">
              <div
                className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin mx-auto"
                style={{ borderColor: '#43a047', borderTopColor: 'transparent' }}
              />
              <p className="text-sm" style={{ color: '#e5e7eb' }}>{t('onboarding.lookingUp')}</p>
            </div>
          </div>
        )}

        {/* Back header */}
        <div
          className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <button
            onClick={handleBackFromImport}
            className="text-sm transition-colors"
            style={{ color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}
            disabled={importBusy}
          >
            ← {t('common.back')}
          </button>
          <h2 className="text-white font-semibold flex-1 text-center">{t('onboarding.importWallet')}</h2>
          <button
            type="button"
            aria-label={t('settings.rpcNode')}
            disabled={importBusy}
            onClick={() => {
              setCustomRpc(rpcUrl)
              setRpcSheetOpen(true)
            }}
            className="flex items-center justify-center"
            style={{
              width: 40,
              height: 40,
              background: 'none',
              border: 'none',
              cursor: importBusy ? 'default' : 'pointer',
              color: '#9ca3af',
              opacity: importBusy ? 0.4 : 1,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
        </div>

        <div className="scroll-area px-4 py-5 space-y-4">
          <p className="text-[11px] truncate" style={{ color: '#6b7280' }}>
            {t('settings.rpcNode')}: {rpcUrl}
          </p>

          {/* Label */}
          <div className="space-y-1.5">
            <label className="section-label block">{t('onboarding.walletLabel')}</label>
            <input
              type="text"
              value={walletLabel}
              onChange={e => setWalletLabel(e.target.value.slice(0, 64))}
              placeholder={t('onboarding.walletLabelPlaceholder')}
              className="input-field"
              autoCapitalize="sentences"
              autoCorrect="on"
              disabled={importBusy}
            />
            <p className="text-xs leading-relaxed" style={{ color: '#6b7280' }}>
              {t('onboarding.walletLabelHint')}
            </p>
          </div>

          {/* Private key */}
          <div className="space-y-1.5">
            <label className="section-label block">{t('onboarding.enterPrivateKey')}</label>
            <textarea
              value={privateKey}
              onChange={e => setPrivateKey(e.target.value)}
              placeholder={t('onboarding.privateKeyPlaceholder')}
              rows={3}
              className="input-field font-mono text-xs resize-none"
              style={{ height: 'auto', paddingTop: 12, paddingBottom: 12 }}
              disabled={importBusy}
            />
          </div>

          {/* Error */}
          {error && (
            <div
              className="text-sm rounded-xl px-3 py-2.5"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}
            >
              {error}
            </div>
          )}

          <button
            className="btn-primary w-full mt-2"
            disabled={importBusy}
            onClick={() => void handleImport()}
            style={{ height: 50 }}
          >
            {t('onboarding.importWallet')}
          </button>

          <p className="text-center text-xs px-2" style={{ color: '#6b7280' }}>
            🔐 {t('onboarding.securityNotice')}
          </p>
        </div>

        {rpcSheetOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
            <button
              type="button"
              className="absolute inset-0"
              style={{ background: 'rgba(0,0,0,0.75)' }}
              aria-label={t('common.close')}
              onClick={() => setRpcSheetOpen(false)}
            />
            <div
              className="relative w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3"
              style={{ background: '#0c100c', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-center justify-between">
                <p className="text-white font-semibold">{t('settings.rpcNode')}</p>
                <button
                  type="button"
                  className="text-gray-400 px-2"
                  onClick={() => setRpcSheetOpen(false)}
                >
                  ✕
                </button>
              </div>
              <p className="text-xs" style={{ color: '#6b7280' }}>
                {t('onboarding.rpcHint')}
              </p>
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {RPC_PRESETS.map(rpc => (
                  <button
                    key={rpc}
                    type="button"
                    onClick={() => {
                      setRpcUrl(rpc)
                      setCustomRpc(rpc)
                      setRpcSheetOpen(false)
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-xl text-xs font-mono border"
                    style={{
                      color: rpcUrl === rpc ? '#86efac' : '#9ca3af',
                      borderColor: rpcUrl === rpc ? 'rgba(67,160,71,0.4)' : 'rgba(255,255,255,0.06)',
                      background: rpcUrl === rpc ? 'rgba(67,160,71,0.1)' : '#141914',
                    }}
                  >
                    {rpc}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={customRpc}
                  onChange={e => setCustomRpc(e.target.value)}
                  placeholder="https://..."
                  className="input-field flex-1 text-xs py-2"
                />
                <button
                  type="button"
                  className="btn-secondary text-xs px-3 py-2"
                  onClick={() => {
                    if (!customRpc.trim()) return
                    setRpcUrl(customRpc.trim())
                    setRpcSheetOpen(false)
                  }}
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── CHOOSE STEP (landing) ───────────────────────────────────
  return (
    <div
      className="relative flex flex-col h-full items-center justify-center px-6 gap-0"
      style={{ background: 'radial-gradient(ellipse at 50% 40%, #04120a 0%, #000000 65%)' }}
    >
      {/* Language switcher */}
      <div className="absolute pt-safe" style={{ top: 16, right: 16, zIndex: 10 }}>
        <LanguageSwitcher />
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Hero */}
      <div className="flex flex-col items-center gap-5 w-full max-w-sm mx-auto">
        {/* Logo */}
        <div className="flex items-center justify-center">
          <img
            src="/hero.png"
            alt="The Proper Wallet"
            className="block"
            style={{ width: 232, height: 232, objectFit: 'contain' }}
            draggable={false}
          />
        </div>

        {/* Title + subtitle */}
        <div className="w-full flex flex-col items-center gap-2">
          <img
            src="/title.png"
            alt="ProperWallet"
            className="block mx-auto"
            style={{
              height: 48,
              width: 'auto',
              maxWidth: 'min(100%, 300px)',
              objectFit: 'contain',
            }}
            draggable={false}
          />
          <p
            className="text-center w-full px-2"
            style={{ fontSize: 14, color: '#5cbf63', opacity: 0.85, lineHeight: 1.45 }}
          >
            {t('onboarding.subtitle')}
          </p>
        </div>
      </div>

      <div className="flex-1" />

      {/* CTAs */}
      <div className="w-full space-y-3 pb-10">
        <button
          className="btn-primary w-full font-bold"
          style={{ height: 54, fontSize: 16, borderRadius: 16, letterSpacing: '0.01em' }}
          onClick={() => setStep('import')}
        >
          {t('onboarding.importWallet')}
        </button>

        {onboardingMode === 'add-account' && (
          <button
            type="button"
            className="w-full text-sm"
            style={{ color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
            onClick={() => navigate('wallet')}
          >
            ← {t('common.back')}
          </button>
        )}

        <p className="text-center text-xs px-4" style={{ color: '#4b5563', lineHeight: 1.6 }}>
          🔐 {t('onboarding.securityNotice')}
        </p>
      </div>
    </div>
  )
}
